'use client';

import { create } from 'zustand';
import type {
  PharmacyCategory,
  PharmacyService,
  PharmacyProvider,
  PharmacyAvailabilityByDate,
  PharmacyAvailabilitySlot,
  CreatePharmacyPaymentIntentResponse,
  ConfirmPaidPharmacyBookingResponse,
  PaidBookingFailedResponse,
} from '@/features/pharamacy/services/pharmacyApi';

// ---------------------------------------------------------------------------
// Display context – visual context from pharmacist advice section (display only)
// ---------------------------------------------------------------------------

export interface PharmacyBookingDisplayContext {
  categoryName?: string;
  categoryImage?: {
    urlDesktop?: string;
    urlMobile?: string;
    alt?: string;
  };
  treatmentName?: string;
  treatmentImage?: {
    urlDesktop?: string;
    urlMobile?: string;
    alt?: string;
    width?: number;
    height?: number;
    className?: string;
  };
}

// ---------------------------------------------------------------------------
// Client form shape
// ---------------------------------------------------------------------------

export interface PharmacyClientForm {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dateOfBirth: string;
}

const EMPTY_CLIENT_FORM: PharmacyClientForm = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  dateOfBirth: '',
};

// ---------------------------------------------------------------------------
// Payment snapshot – frozen at PaymentIntent creation time
// ---------------------------------------------------------------------------

export interface PaymentSnapshotData {
  serviceId: number;
  providerId: number;
  startDatetime: string;
  categoryId?: number;
  client: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    dateOfBirth?: string;
  };
}

// ---------------------------------------------------------------------------
// Full store type
// ---------------------------------------------------------------------------

export interface PharmacyBookingState {
  // -- Data --
  categories: PharmacyCategory[];
  services: PharmacyService[];
  providers: PharmacyProvider[];
  availability: PharmacyAvailabilityByDate;

  // -- Selections --
  selectedCategoryId: number | null;
  selectedServiceId: number | null;
  selectedDate: string | null;
  selectedTime: string | null;
  selectedProviderId: number | null;
  firstAvailableDate: string | null;

  // -- Patient form --
  clientForm: PharmacyClientForm;

  // -- Result --
  bookingResult: ConfirmPaidPharmacyBookingResponse | null;
  paidBookingFailure: PaidBookingFailedResponse | null;

  // -- Display context (visual-only, from pharmacist advice section) --
  displayContext: PharmacyBookingDisplayContext | null;
  displayContextMap: Record<number, PharmacyBookingDisplayContext>;
  categoryDisplayContextMap: Record<string, PharmacyBookingDisplayContext>;

  // -- Payment --
  paymentStepReady: boolean;
  isProcessingPayment: boolean;
  clientSecret: string | null;
  paymentIntentId: string | null;
  paymentAmount: number | null;
  paymentCurrency: string | null;
  paymentError: string | null;
  bookingError: string | null;
  paymentSnapshot: PaymentSnapshotData | null;

  // -- Status --
  isLoadingServices: boolean;
  isLoadingAvailability: boolean;
  isSubmittingBooking: boolean;
  error: string | null;
  successMessage: string | null;

  // -- Actions --
  setCategories: (categories: PharmacyCategory[]) => void;
  setServices: (services: PharmacyService[]) => void;
  setProviders: (providers: PharmacyProvider[]) => void;
  setAvailability: (availability: PharmacyAvailabilityByDate) => void;
  setSelectedCategoryId: (categoryId: number | null) => void;
  setSelectedServiceId: (serviceId: number | null) => void;
  setSelectedDate: (date: string | null) => void;
  setSelectedTime: (time: string | null) => void;
  setSelectedProviderId: (providerId: number | null) => void;
  setFirstAvailableDate: (date: string | null) => void;
  setClientFormValue: (field: keyof PharmacyClientForm, value: string) => void;
  setIsLoadingServices: (loading: boolean) => void;
  setIsLoadingAvailability: (loading: boolean) => void;
  setIsSubmittingBooking: (loading: boolean) => void;
  setBookingResult: (result: ConfirmPaidPharmacyBookingResponse | null) => void;
  setPaidBookingFailure: (failure: PaidBookingFailedResponse | null) => void;
  clearPaidBookingFailure: () => void;
  setError: (message: string | null) => void;
  clearError: () => void;
  setPaymentStepReady: (ready: boolean) => void;
  setIsProcessingPayment: (loading: boolean) => void;
  setClientSecret: (secret: string | null) => void;
  setPaymentIntentId: (id: string | null) => void;
  setPaymentAmount: (amount: number | null) => void;
  setPaymentCurrency: (currency: string | null) => void;
  setPaymentError: (message: string | null) => void;
  setBookingError: (message: string | null) => void;
  clearPaymentState: () => void;
  clearStalePaymentIntent: () => void;
  setPaymentSnapshot: (snapshot: PaymentSnapshotData | null) => void;
  removeSlotFromAvailability: (date: string, time: string) => void;
  setPaymentIntentResult: (
    result: CreatePharmacyPaymentIntentResponse,
    snapshot: PaymentSnapshotData | null,
  ) => void;
  setDisplayContext: (context: PharmacyBookingDisplayContext | null) => void;
  setDisplayContextMap: (
    map: Record<number, PharmacyBookingDisplayContext>,
  ) => void;
  setCategoryDisplayContextMap: (
    map: Record<string, PharmacyBookingDisplayContext>,
  ) => void;
  resetBookingFlow: () => void;
  resetBookingSelections: () => void;
  resetBookingModalFlow: () => void;
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const usePharmacyBookingStore = create<PharmacyBookingState>()(
  (set) => ({
    // -- Data --
    categories: [],
    services: [],
    providers: [],
    availability: {},

    // -- Selections --
    selectedCategoryId: null,
    selectedServiceId: null,
    selectedDate: null,
    selectedTime: null,
    selectedProviderId: null,
    firstAvailableDate: null,

    // -- Patient form --
    clientForm: { ...EMPTY_CLIENT_FORM },

    // -- Payment --
    paymentStepReady: false,
    isProcessingPayment: false,
    clientSecret: null,
    paymentIntentId: null,
    paymentAmount: null,
    paymentCurrency: null,
    paymentError: null,
    bookingError: null,
    paymentSnapshot: null,

    // -- Result --
    bookingResult: null,
    paidBookingFailure: null,

    // -- Display context (visual-only, from pharmacist advice section) --
    displayContext: null,
    displayContextMap: {},
    categoryDisplayContextMap: {},

    // -- Status --
    isLoadingServices: false,
    isLoadingAvailability: false,
    isSubmittingBooking: false,
    error: null,
    successMessage: null,

    // -- Actions --
    setCategories: (categories) => set({ categories }),

    setServices: (services) => set({ services }),

    setProviders: (providers) => set({ providers }),

    setAvailability: (availability) => set({ availability }),

    setSelectedCategoryId: (categoryId) =>
      set({
        selectedCategoryId: categoryId,
        selectedServiceId: null,
        selectedDate: null,
        selectedTime: null,
        selectedProviderId: null,
        firstAvailableDate: null,
        availability: {},
        bookingResult: null,
        paidBookingFailure: null,
        paymentStepReady: false,
        clientSecret: null,
        paymentIntentId: null,
        paymentAmount: null,
        paymentCurrency: null,
        paymentError: null,
        bookingError: null,
        paymentSnapshot: null,
      }),

    setSelectedServiceId: (serviceId) =>
      set({
        selectedServiceId: serviceId,
        selectedDate: null,
        selectedTime: null,
        selectedProviderId: null,
        firstAvailableDate: null,
        availability: {},
        bookingResult: null,
        paidBookingFailure: null,
        paymentStepReady: false,
        clientSecret: null,
        paymentIntentId: null,
        paymentAmount: null,
        paymentCurrency: null,
        paymentError: null,
        bookingError: null,
        paymentSnapshot: null,
      }),

    setSelectedDate: (date) =>
      set({
        selectedDate: date,
        selectedTime: null,
        selectedProviderId: null,
        paymentStepReady: false,
        clientSecret: null,
        paymentIntentId: null,
        paymentAmount: null,
        paymentCurrency: null,
        paymentError: null,
        bookingError: null,
        paymentSnapshot: null,
      }),

    setSelectedTime: (time) =>
      set({
        selectedTime: time,
        paymentStepReady: false,
        clientSecret: null,
        paymentIntentId: null,
        paymentAmount: null,
        paymentCurrency: null,
        paymentError: null,
        bookingError: null,
        paymentSnapshot: null,
      }),

    setSelectedProviderId: (providerId) =>
      set({ selectedProviderId: providerId, paymentSnapshot: null }),

    setFirstAvailableDate: (date) => set({ firstAvailableDate: date }),

    setClientFormValue: (field, value) =>
      set((state) => ({
        clientForm: { ...state.clientForm, [field]: value },
        paymentSnapshot: null,
      })),

    setIsLoadingServices: (loading) => set({ isLoadingServices: loading }),

    setIsLoadingAvailability: (loading) =>
      set({ isLoadingAvailability: loading }),

    setIsSubmittingBooking: (loading) => set({ isSubmittingBooking: loading }),

    setBookingResult: (result) => set({ bookingResult: result }),

    setPaidBookingFailure: (failure) => set({ paidBookingFailure: failure }),

    clearPaidBookingFailure: () => set({ paidBookingFailure: null }),

    setError: (message) => set({ error: message, successMessage: null }),

    clearError: () => set({ error: null }),

    setPaymentStepReady: (ready) => set({ paymentStepReady: ready }),

    setIsProcessingPayment: (loading) => set({ isProcessingPayment: loading }),

    setClientSecret: (secret) => set({ clientSecret: secret }),

    setPaymentIntentId: (id) => set({ paymentIntentId: id }),

    setPaymentAmount: (amount) => set({ paymentAmount: amount }),

    setPaymentCurrency: (currency) => set({ paymentCurrency: currency }),

    setPaymentError: (message) => set({ paymentError: message }),

    setBookingError: (message) => set({ bookingError: message }),

    clearPaymentState: () =>
      set({
        paymentStepReady: false,
        isProcessingPayment: false,
        clientSecret: null,
        paymentIntentId: null,
        paymentAmount: null,
        paymentCurrency: null,
        paymentError: null,
        bookingError: null,
        paymentSnapshot: null,
        paidBookingFailure: null,
      }),

    clearStalePaymentIntent: () =>
      set({
        clientSecret: null,
        paymentIntentId: null,
        paymentAmount: null,
        paymentCurrency: null,
        paymentSnapshot: null,
      }),

    setPaymentSnapshot: (snapshot) => set({ paymentSnapshot: snapshot }),

    removeSlotFromAvailability: (date: string, time: string) =>
      set((state) => {
        const day = state.availability[date];
        if (!day) return {};
        return {
          availability: {
            ...state.availability,
            [date]: {
              ...day,
              slots: day.slots.filter((s) => s.time !== time),
            },
          },
        };
      }),

    setPaymentIntentResult: (result, snapshot) =>
      set({
        clientSecret: result.clientSecret,
        paymentIntentId: result.paymentIntentId,
        paymentAmount: result.amount,
        paymentCurrency: result.currency,
        paymentSnapshot: snapshot,
      }),

    setDisplayContext: (context) => set({ displayContext: context }),

    setDisplayContextMap: (map) => set({ displayContextMap: map }),

    setCategoryDisplayContextMap: (map) =>
      set({ categoryDisplayContextMap: map }),

    resetBookingFlow: () =>
      set({
        selectedCategoryId: null,
        selectedServiceId: null,
        selectedDate: null,
        selectedTime: null,
        selectedProviderId: null,
        firstAvailableDate: null,
        availability: {},
        clientForm: { ...EMPTY_CLIENT_FORM },
        bookingResult: null,
        paidBookingFailure: null,
        displayContext: null,
        displayContextMap: {},
        categoryDisplayContextMap: {},
        isLoadingServices: false,
        isLoadingAvailability: false,
        isSubmittingBooking: false,
        error: null,
        successMessage: null,
        paymentStepReady: false,
        isProcessingPayment: false,
        clientSecret: null,
        paymentIntentId: null,
        paymentAmount: null,
        paymentCurrency: null,
        paymentError: null,
        bookingError: null,
        paymentSnapshot: null,
      }),

    resetBookingSelections: () =>
      set({
        selectedCategoryId: null,
        selectedServiceId: null,
        selectedDate: null,
        selectedTime: null,
        selectedProviderId: null,
        firstAvailableDate: null,
        availability: {},
        clientForm: { ...EMPTY_CLIENT_FORM },
        bookingResult: null,
        paidBookingFailure: null,
        displayContext: null,
        displayContextMap: {},
        categoryDisplayContextMap: {},
        isSubmittingBooking: false,
        error: null,
        successMessage: null,
        paymentStepReady: false,
        isProcessingPayment: false,
        clientSecret: null,
        paymentIntentId: null,
        paymentAmount: null,
        paymentCurrency: null,
        paymentError: null,
        bookingError: null,
        paymentSnapshot: null,
      }),

    resetBookingModalFlow: () =>
      set({
        selectedCategoryId: null,
        selectedServiceId: null,
        selectedDate: null,
        selectedTime: null,
        selectedProviderId: null,
        firstAvailableDate: null,
        availability: {},
        clientForm: { ...EMPTY_CLIENT_FORM },
        bookingResult: null,
        paidBookingFailure: null,
        displayContext: null,
        isSubmittingBooking: false,
        error: null,
        successMessage: null,
        paymentStepReady: false,
        isProcessingPayment: false,
        clientSecret: null,
        paymentIntentId: null,
        paymentAmount: null,
        paymentCurrency: null,
        paymentError: null,
        bookingError: null,
        paymentSnapshot: null,
      }),
  }),
);

// ---------------------------------------------------------------------------
// Derived selectors (pure functions that read store state)
// ---------------------------------------------------------------------------

export function getSelectedCategory(
  state: PharmacyBookingState,
): PharmacyCategory | null {
  if (state.selectedCategoryId === null) return null;
  return (
    state.categories.find((c) => c.id === state.selectedCategoryId) ?? null
  );
}

export function getSelectedService(
  state: PharmacyBookingState,
): PharmacyService | null {
  if (state.selectedServiceId === null) return null;
  return state.services.find((s) => s.id === state.selectedServiceId) ?? null;
}

export function getSelectedDateSlots(
  state: PharmacyBookingState,
): PharmacyAvailabilitySlot[] {
  if (!state.selectedDate) return [];
  const day = state.availability[state.selectedDate];
  return day?.slots ?? [];
}

export function getSelectedSlot(
  state: PharmacyBookingState,
): PharmacyAvailabilitySlot | null {
  if (!state.selectedTime) return null;
  const slots = getSelectedDateSlots(state);
  return slots.find((s) => s.time === state.selectedTime) ?? null;
}

export function canContinueToAvailability(
  state: PharmacyBookingState,
): boolean {
  if (state.selectedServiceId === null) return false;
  const service = getSelectedService(state);
  if (!service) return false;
  return service.providers.length > 0;
}

export function canContinueToPatientForm(state: PharmacyBookingState): boolean {
  return (
    state.selectedServiceId !== null &&
    state.selectedDate !== null &&
    state.selectedTime !== null &&
    state.selectedProviderId !== null
  );
}

export function canSubmitBooking(state: PharmacyBookingState): boolean {
  const { clientForm } = state;
  return (
    state.selectedServiceId !== null &&
    state.selectedDate !== null &&
    state.selectedTime !== null &&
    state.selectedProviderId !== null &&
    clientForm.firstName.trim().length > 0 &&
    clientForm.lastName.trim().length > 0 &&
    clientForm.email.trim().length > 0 &&
    clientForm.phone.trim().length > 0
  );
}

// ---------------------------------------------------------------------------
// Utility helpers
// ---------------------------------------------------------------------------

export function buildClientName(clientForm: PharmacyClientForm): string {
  return `${clientForm.firstName} ${clientForm.lastName}`.trim();
}

export function buildStartDatetime(date: string, time: string): string {
  const normalizedTime = time.includes(':')
    ? time.split(':').length === 2
      ? `${time}:00`
      : time
    : `${time}:00`;
  return `${date} ${normalizedTime}`;
}
